(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.VaultOrganizer = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const MAX_TEXT_CHARS = 250000;
  const MAX_FILE_BYTES = 1048576;
  const MAX_RECORDS = 500;
  const MAX_FIELDS_PER_RECORD = 100;
  const MAX_TOTAL_FIELDS = 5000;
  const aliases = {
    name: ['名称', '网站名称', '应用名称', '网站', '应用', '标题', '平台', 'name'],
    url: ['网址', '网站地址', '登录网址', '登录地址', '链接', 'url', 'uri', 'login_uri'],
    account: ['账号', '帐号', '账户', '帐户', '登录账号', '登录帐号', '登录账户', '登陆账号', 'account', 'login', 'login_username'],
    password: ['密码', '登录密码', 'password', 'passwd', 'pwd', 'login_password'],
    username: ['用户名', '用户名称', '昵称', '姓名', '注册名', 'username', 'displayname'],
    phone: ['手机号', '手机号码', '手机', '电话', '联系电话', 'phone', 'mobile'],
    email: ['邮箱', '电子邮箱', '邮件地址', 'email', 'e-mail'],
    notes: ['备注', '说明', '补充信息', 'notes', 'note']
  };
  const keys = new Map();
  for (const [canonical, names] of Object.entries(aliases)) {
    for (const name of names) keys.set(name.toLowerCase(), canonical);
  }
  const canonicalKey = label => keys.get(String(label).trim().toLowerCase()) || null;
  const isSensitive = label => /密码|密钥|令牌|恢复|备份码|安全答案|口令|token|secret|password|recovery/i.test(label);
  const warn = (record, code, message) => {
    if (!record.warnings.some(item => item.code === code && item.message === message)) record.warnings.push({ code, message });
  };
  function blankRecord() {
    return {
      name: '', url: '', account: '', password: '', username: '', phone: '', email: '', notes: '',
      extraFields: [], unparsed: [], warnings: [], _seen: Object.create(null), _fieldCount: 0
    };
  }
  function addField(record, label, value, line) {
    if (++record._fieldCount > MAX_FIELDS_PER_RECORD) throw new Error(`一条记录最多 ${MAX_FIELDS_PER_RECORD} 个字段，请拆分后整理；本次未生成结果。`);
    const fieldName = String(label).trim() || '未命名字段';
    const canonical = canonicalKey(fieldName);
    const seenKey = canonical || `extra:${fieldName}`;
    record._seen[seenKey] = (record._seen[seenKey] || 0) + 1;
    const occurrence = record._seen[seenKey];
    if (occurrence > 1) {
      record.extraFields.push({ name: `${fieldName}（重复 ${occurrence}）`, value, hidden: !canonical || canonical === 'password' || isSensitive(fieldName) });
      warn(record, 'duplicate-field', `第 ${line} 行的“${fieldName}”重复，已另外保留；请选择正确值。`);
    } else if (canonical) record[canonical] = value;
    else {
      record.extraFields.push({ name: fieldName, value, hidden: true });
      warn(record, 'unknown-field', `“${fieldName}”未匹配标准字段，已保留为自定义字段。`);
    }
  }
  function safeUrl(value) {
    const candidate = String(value || '').trim();
    if (!candidate) return null;
    try {
      const parsed = new URL(candidate);
      if (!['https:', 'http:'].includes(parsed.protocol) || !parsed.hostname || parsed.username || parsed.password) return null;
      return candidate;
    } catch (_) { return null; }
  }
  function reviewRecord(record, index) {
    if (record.password && /^\s|\s$/.test(record.password)) warn(record, 'password-space', '密码开头或结尾有空白，已原样保留；请确认空白是否属于密码。');
    if (/[\r\n]/.test(record.password)) warn(record, 'password-newline', '密码包含换行，已原样保留；展开后若编辑，浏览器会统一换行格式。');
    if (record.account && /^\s|\s$/.test(record.account)) warn(record, 'account-space', '账号开头或结尾有空白，已原样保留；请确认。');
    if (record.url && !safeUrl(record.url)) warn(record, 'invalid-url', '网址须为不含用户名或密码的完整 http:// 或 https:// 地址；当前值将保留在自定义字段中。');
    if (!record.name.trim()) {
      const url = safeUrl(record.url);
      record.name = url ? new URL(url).hostname : `未命名记录 ${index + 1}`;
      warn(record, 'missing-name', '未提供名称，已生成临时名称；可在预览中修改。');
    }
    if (record.unparsed.length) warn(record, 'unparsed-lines', `${record.unparsed.length} 行无法确定字段，已保留原文并加入导入备注；请展开检查。`);
    delete record._seen;
    delete record._fieldCount;
    return record;
  }
  function checkDuplicateAccounts(records) {
    const seen = new Map();
    records.forEach((record, index) => {
      const account = record.account || record.username;
      if (!account) return;
      if (seen.has(account)) {
        const previous = seen.get(account);
        warn(record, 'duplicate-account', `此登录账号也出现在第 ${previous + 1} 条，已保留独立条目，没有合并。`);
        warn(records[previous], 'duplicate-account', `此登录账号也出现在第 ${index + 1} 条，已保留独立条目，没有合并。`);
      } else seen.set(account, index);
    });
  }
  function parseText(source) {
    const records = [];
    let current = blankRecord();
    let populated = false;
    const finish = () => {
      if (!populated) return;
      if (records.length >= MAX_RECORDS) throw new Error(`最多整理 ${MAX_RECORDS} 条记录，请分批处理；本次未生成结果。`);
      records.push(current);
      current = blankRecord();
      populated = false;
    };
    source.split(/\r\n|\n|\r/).forEach((line, index) => {
      if (!line.trim() || /^\s*(?:-{3,}|={3,}|\*{3,}|记录\s*\d+\s*[:：]?)\s*$/.test(line)) { finish(); return; }
      populated = true;
      const match = /^\s*([^:：=＝]{1,80}?)\s*[:：=＝](.*)$/.exec(line);
      if (match && !/^\s*https?:\/\//i.test(line)) {
        addField(current, match[1], match[2], index + 1);
        if (canonicalKey(match[1]) !== 'password' && /\s+(?:账号|帐号|用户名|手机号|邮箱|密码|网址)\s*[:：=＝]/.test(match[2])) {
          warn(current, 'inline-fields', `第 ${index + 1} 行可能同时包含多个字段，未自动拆分；请改为每行一个字段或在预览中调整。`);
        }
      } else current.unparsed.push({ line: index + 1, text: line });
    });
    finish();
    return records;
  }
  // RFC 4180-style CSV, also used for TSV; values inside quotes retain exact line endings.
  function readDelimited(source, delimiter) {
    const rows = [];
    let cells = [], value = '', quoted = false, afterQuote = false, atStart = true;
    let line = 1, rowLine = 1;
    const finishCell = () => { cells.push(value); value = ''; atStart = true; afterQuote = false; };
    const finishRow = () => { finishCell(); rows.push({ cells, line: rowLine }); cells = []; rowLine = line; };
    for (let index = 0; index < source.length; index++) {
      const character = source[index];
      if (quoted) {
        if (character === '"') {
          if (source[index + 1] === '"') { value += '"'; index++; }
          else { quoted = false; afterQuote = true; }
        } else {
          value += character;
          if (character === '\n' || (character === '\r' && source[index + 1] !== '\n')) line++;
        }
        continue;
      }
      if (character === delimiter) { finishCell(); continue; }
      if (character === '\n' || character === '\r') {
        if (character === '\r' && source[index + 1] === '\n') index++;
        line++;
        finishRow();
        continue;
      }
      if (afterQuote) throw new Error(`第 ${line} 行闭合引号后出现额外字符，请修正分隔文件；本次未生成结果。`);
      if (character === '"') {
        if (!atStart) throw new Error(`第 ${line} 行未加引号的字段中出现引号，请修正；本次未生成结果。`);
        quoted = true; atStart = false;
      } else { value += character; atStart = false; }
    }
    if (quoted) throw new Error('文件中存在未闭合的引号，请修正后重新整理；本次未生成结果。');
    if (value.length || cells.length || afterQuote || !atStart) finishRow();
    return rows;
  }
  function parseDelimited(source, delimiter) {
    const rows = readDelimited(source, delimiter).filter(row => row.cells.some(value => value !== ''));
    if (!rows.length) return [];
    const header = rows.shift();
    const labels = header.cells.map((cell, index) => cell.trim() || `未命名列 ${index + 1}`);
    const knownColumns = labels.filter(canonicalKey).length;
    if (rows.length > MAX_RECORDS) throw new Error(`最多整理 ${MAX_RECORDS} 条记录，请分批处理；本次未生成结果。`);
    return rows.map(row => {
      const record = blankRecord();
      if (!knownColumns) warn(record, 'unknown-header', '表头未包含已知字段；所有列已保留为自定义字段，请核对表头。');
      if (row.cells.length !== labels.length) warn(record, 'column-count', `第 ${row.line} 行有 ${row.cells.length} 列，表头有 ${labels.length} 列；缺少列留空，多余列已另外保留。`);
      for (let index = 0; index < Math.max(labels.length, row.cells.length); index++) {
        addField(record, labels[index] || `多余列 ${index + 1}`, row.cells[index] === undefined ? '' : row.cells[index], row.line);
      }
      return record;
    });
  }
  function detectFormat(source, filename) {
    if (/\.csv$/i.test(filename || '')) return 'csv';
    if (/\.tsv$/i.test(filename || '')) return 'tsv';
    const firstLine = source.split(/\r\n|\n|\r/, 1)[0];
    // A tab may simply indent a labelled value; labelled rows take precedence.
    if (/^\s*[^:\t,=：＝]{1,80}?\s*[:：=＝]/.test(firstLine)) return 'text';
    if (firstLine.includes('\t')) {
      try { if (readDelimited(firstLine, '\t')[0].cells.some(canonicalKey)) return 'tsv'; } catch (_) { /* Preserve ambiguous content as text unless a format is selected explicitly. */ }
    }
    if (firstLine.includes(',')) {
      try { if (readDelimited(firstLine, ',')[0].cells.some(canonicalKey)) return 'csv'; } catch (_) { /* Main parser reports errors when format is chosen explicitly. */ }
    }
    return 'text';
  }
  function parse(source, options) {
    options = options || {};
    if (typeof source !== 'string') throw new Error('需要提供文本内容。');
    if (source.length > MAX_TEXT_CHARS) throw new Error(`内容超过 ${MAX_TEXT_CHARS.toLocaleString('zh-CN')} 个字符，请分批处理；本次未生成结果。`);
    source = source.replace(/^\uFEFF/, '');
    const format = !options.format || options.format === 'auto' ? detectFormat(source, options.filename) : options.format;
    if (!['text', 'csv', 'tsv'].includes(format)) throw new Error('不支持此格式。');
    const rawRecords = format === 'text' ? parseText(source) : parseDelimited(source, format === 'csv' ? ',' : '\t');
    if (rawRecords.reduce((sum, record) => sum + record._fieldCount, 0) > MAX_TOTAL_FIELDS) throw new Error(`一次最多 ${MAX_TOTAL_FIELDS.toLocaleString('zh-CN')} 个字段，请分批处理；本次未生成结果。`);
    const records = rawRecords.map(reviewRecord);
    checkDuplicateAccounts(records);
    return { format, records };
  }
  function buildExport(records) {
    if (!Array.isArray(records) || !records.length) throw new Error('请先整理并检查至少一条记录。');
    if (records.length > MAX_RECORDS) throw new Error(`最多导出 ${MAX_RECORDS} 条记录。`);
    return {
      encrypted: false,
      folders: [],
      items: records.map((record, index) => {
        const fields = [];
        for (const [key, label] of [['username', '用户名'], ['phone', '手机号'], ['email', '邮箱']]) {
          if (record[key]) fields.push({ name: label, value: record[key], type: 0 });
        }
        const url = safeUrl(record.url);
        if (record.url && (!url || url !== record.url)) fields.push({ name: url ? '原始网址' : '网址（需确认）', value: record.url, type: 0 });
        for (const field of record.extraFields || []) fields.push({ name: field.name || '未命名字段', value: field.value, type: field.hidden ? 1 : 0 });
        const unparsed = (record.unparsed || []).map(item => `第 ${item.line} 行：${item.text}`).join('\n');
        const notes = [record.notes, unparsed ? `未识别原文（请确认）：\n${unparsed}` : ''].filter(Boolean).join('\n\n');
        return {
          organizationId: null, folderId: null, type: 1, reprompt: 0,
          name: record.name.trim() ? record.name : `未命名记录 ${index + 1}`,
          notes: notes || null, favorite: false, fields,
          login: { uris: url ? [{ match: null, uri: url }] : [], username: record.account || record.username || '', password: record.password, totp: null },
          collectionIds: null
        };
      })
    };
  }
  return { parse, buildExport, safeUrl, MAX_TEXT_CHARS, MAX_FILE_BYTES, MAX_RECORDS, MAX_FIELDS_PER_RECORD, MAX_TOTAL_FIELDS };
});
